/* eslint-disable max-lines */
/**
 * Network Insights Panel Component
 * Provides analytics and insights about the relationship network
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Alert,
  Collapse,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import {
  Insights as InsightsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Hub as HubIcon,
  AccountTree as NetworkIcon,
  EmojiObjects as IdeaIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import useTokenExaminerStore from '../stores/tokenExaminerStore';
import {
  getFaanaHealthColor,
  formatDimensionName,
  getDimensionColor,
  getActionableInsight,
} from '../utils/faanaScoring';
import { getContrastText } from '../utils/colorContrast';

// eslint-disable-next-line max-lines-per-function
function NetworkInsightsPanel() {
  const [activeTab, setActiveTab] = useState(0);
  const [expanded, setExpanded] = useState(true);

  const {
    _relationships,
    entities,
    _selectedEntity,
    getFilteredRelationships,
  } = useTokenExaminerStore();

  const filteredRelationships = getFilteredRelationships();

  // Calculate network insights
  const insights = useMemo(() => {
    const totalRelationships = filteredRelationships.length;
    const confirmedRelationships = filteredRelationships.filter(r => r.isConfirmed).length;
    const totalEntities = entities.size;

    // Health distribution
    const healthDistribution = {
      thriving: filteredRelationships.filter(r => (r.overallHealth || 0) >= 80).length,
      healthy: filteredRelationships.filter(r => (r.overallHealth || 0) >= 60 && (r.overallHealth || 0) < 80).length,
      concerning: filteredRelationships.filter(r => (r.overallHealth || 0) >= 40 && (r.overallHealth || 0) < 60).length,
      critical: filteredRelationships.filter(r => (r.overallHealth || 0) < 40).length,
    };

    // Relationship types analysis
    const typeDistribution = {};
    filteredRelationships.forEach(rel => {
      const type = rel.relationshipType;
      typeDistribution[type] = (typeDistribution[type] || 0) + 1;
    });

    // Most connected entities
    const entityConnections = {};
    filteredRelationships.forEach(rel => {
      const sourceHash = rel.sourceEntityHash;
      const targetHash = rel.targetEntityHash;

      entityConnections[sourceHash] = (entityConnections[sourceHash] || 0) + 1;
      entityConnections[targetHash] = (entityConnections[targetHash] || 0) + 1;
    });

    const mostConnected = Object.entries(entityConnections)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([hash, count]) => {
        const entity = Array.from(entities.values()).find(e => e.crossEntityHash === hash);
        return {
          entity: entity || { name: `Entity ${hash.slice(0, 8)}`, type: 'UNKNOWN' },
          connections: count,
        };
      });

    // Risk analysis
    const criticalUnconfirmed = filteredRelationships.filter(r =>
      !r.isConfirmed && (r.overallHealth || 0) < 40
    ).length;

    const highConfidenceUnconfirmed = filteredRelationships.filter(r =>
      !r.isConfirmed && r.confidence === 'HIGH'
    ).length;

    const decliningTrends = filteredRelationships.filter(r =>
      r.trajectoryPrediction === 'weakening'
    ).length;

    // Network density
    const possibleConnections = totalEntities * (totalEntities - 1) / 2;
    const networkDensity = possibleConnections > 0 ?
      (totalRelationships / possibleConnections) * 100 : 0;

    // Average health by dimension
    const dimensionAverages = {};
    const dimensionCounts = {};

    filteredRelationships.forEach(rel => {
      if (rel.dimensions) {
        Object.entries(rel.dimensions).forEach(([dim, data]) => {
          if (data.score !== undefined) {
            dimensionAverages[dim] = (dimensionAverages[dim] || 0) + data.score;
            dimensionCounts[dim] = (dimensionCounts[dim] || 0) + 1;
          }
        });
      }
    });

    Object.keys(dimensionAverages).forEach(dim => {
      dimensionAverages[dim] = dimensionAverages[dim] / dimensionCounts[dim];
    });

    // Growth opportunities
    const growthOpportunities = filteredRelationships
      .filter(r => r.trajectoryPrediction === 'strengthening' && !r.isConfirmed)
      .slice(0, 5);

    return {
      totalRelationships,
      confirmedRelationships,
      totalEntities,
      healthDistribution,
      typeDistribution,
      mostConnected,
      criticalUnconfirmed,
      highConfidenceUnconfirmed,
      decliningTrends,
      networkDensity,
      dimensionAverages,
      growthOpportunities,
      confirmationRate: totalRelationships > 0 ? (confirmedRelationships / totalRelationships) * 100 : 0,
      averageHealth: totalRelationships > 0 ?
        filteredRelationships.reduce((sum, r) => sum + (r.overallHealth || 0), 0) / totalRelationships : 0,
    };
  }, [filteredRelationships, entities]);

  // eslint-disable-next-line max-lines-per-function
  const renderOverviewTab = () => (
    <Box sx={{ p: 2 }}>
      {/* Key Metrics */}
      <Typography variant="subtitle2" gutterBottom>
        Network Overview
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 3 }}>
        <Card variant="outlined">
          <CardContent sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {insights.totalRelationships}
            </Typography>
            <Typography variant="caption">Relationships</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="secondary">
              {insights.totalEntities}
            </Typography>
            <Typography variant="caption">Entities</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent sx={{ p: 2, textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{ color: getFaanaHealthColor(insights.confirmationRate) }}
            >
              {Math.round(insights.confirmationRate)}%
            </Typography>
            <Typography variant="caption">Confirmed</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ color: getFaanaHealthColor(insights.networkDensity) }}>
              {Math.round(insights.networkDensity)}%
            </Typography>
            <Typography variant="caption">Density</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Health Distribution */}
      <Typography variant="subtitle2" gutterBottom>
        Relationship Health Distribution
      </Typography>

      <Box sx={{ mb: 3 }}>
        {Object.entries(insights.healthDistribution).map(([category, count]) => (
          <Box key={category} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {category}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {count} ({insights.totalRelationships > 0 ? Math.round((count / insights.totalRelationships) * 100) : 0}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={insights.totalRelationships > 0 ? (count / insights.totalRelationships) * 100 : 0}
              sx={{
                height: 6,
                borderRadius: 3,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getFaanaHealthColor(
                    category === 'thriving' ? 90 :
                    category === 'healthy' ? 70 :
                    category === 'concerning' ? 50 : 30
                  ),
                },
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Most Connected Entities */}
      <Typography variant="subtitle2" gutterBottom>
        Most Connected Entities
      </Typography>

      <List dense>
        {insights.mostConnected.map((item, idx) => (
          <ListItem key={idx}>
            <ListItemIcon>
              <HubIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={item.entity.name}
              secondary={`${item.connections} connections • ${item.entity.type}`}
            />
            <Chip
              label={item.connections}
              size="small"
              color="primary"
              variant="outlined"
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const renderRisksTab = () => (
    <Box sx={{ p: 2 }}>
      {/* Risk Alerts */}
      <Typography variant="subtitle2" gutterBottom>
        Risk Assessment
      </Typography>

      {insights.criticalUnconfirmed > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {insights.criticalUnconfirmed} Critical Unconfirmed Relationships
          </Typography>
          <Typography variant="body2">
            These relationships have low health scores and need immediate attention.
          </Typography>
        </Alert>
      )}

      {insights.decliningTrends > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {insights.decliningTrends} Declining Relationships
          </Typography>
          <Typography variant="body2">
            These relationships show weakening trends and may need intervention.
          </Typography>
        </Alert>
      )}

      {insights.highConfidenceUnconfirmed > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {insights.highConfidenceUnconfirmed} High-Confidence Pending
          </Typography>
          <Typography variant="body2">
            These relationships have high confidence scores and should be prioritized for confirmation.
          </Typography>
        </Alert>
      )}

      {/* Risk Breakdown */}
      <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
        Risk Categories
      </Typography>

      <List dense>
        <ListItem>
          <ListItemIcon>
            <WarningIcon sx={{ color: '#F44336' }} />
          </ListItemIcon>
          <ListItemText
            primary="Critical Health"
            secondary={`${insights.healthDistribution.critical} relationships below 40% health`}
          />
          <Chip
            label={insights.healthDistribution.critical}
            size="small"
            color="error"
            variant={insights.healthDistribution.critical > 0 ? "filled" : "outlined"}
          />
        </ListItem>

        <ListItem>
          <ListItemIcon>
            <TrendingDownIcon sx={{ color: '#FF9800' }} />
          </ListItemIcon>
          <ListItemText
            primary="Declining Trends"
            secondary={`${insights.decliningTrends} relationships showing negative trajectory`}
          />
          <Chip
            label={insights.decliningTrends}
            size="small"
            color="warning"
            variant={insights.decliningTrends > 0 ? "filled" : "outlined"}
          />
        </ListItem>

        <ListItem>
          <ListItemIcon>
            <CheckCircleIcon sx={{ color: '#2196F3' }} />
          </ListItemIcon>
          <ListItemText
            primary="Confirmation Backlog"
            secondary={`${insights.totalRelationships - insights.confirmedRelationships} relationships need confirmation`}
          />
          <Chip
            label={insights.totalRelationships - insights.confirmedRelationships}
            size="small"
            color="info"
            variant={insights.totalRelationships - insights.confirmedRelationships > 0 ? "filled" : "outlined"}
          />
        </ListItem>
      </List>
    </Box>
  );

  const renderOpportunitiesTab = () => (
    <Box sx={{ p: 2 }}>
      {/* Growth Opportunities */}
      <Typography variant="subtitle2" gutterBottom>
        Growth Opportunities
      </Typography>

      {insights.growthOpportunities.length > 0 ? (
        <List dense>
          {insights.growthOpportunities.map((rel, idx) => (
            <ListItem key={idx}>
              <ListItemIcon>
                <TrendingUpIcon sx={{ color: '#4CAF50' }} />
              </ListItemIcon>
              <ListItemText
                primary={rel.relationshipType}
                secondary={`${rel.sourceEntity?.name} → ${rel.targetEntity?.name} • Strengthening trend`}
              />
              <Chip
                label={`${rel.overallHealth || 0}%`}
                size="small"
                sx={{
                  backgroundColor: getFaanaHealthColor(rel.overallHealth || 0),
                  color: getContrastText(getFaanaHealthColor(rel.overallHealth || 0)),
                }}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Alert severity="info">
          No growth opportunities identified. Complete more relationship analysis to discover trends.
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Dimension Insights */}
      <Typography variant="subtitle2" gutterBottom>
        Dimension Analysis
      </Typography>

      {Object.entries(insights.dimensionAverages).length > 0 ? (
        <Box>
          {Object.entries(insights.dimensionAverages).map(([dimension, average]) => {
            const insight = getActionableInsight(dimension, average);
            return (
              <Card key={dimension} variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      {formatDimensionName(dimension)}
                    </Typography>
                    <Chip
                      label={`${average.toFixed(1)}/10`}
                      size="small"
                      sx={{
                        backgroundColor: getDimensionColor(average),
                        color: getContrastText(getDimensionColor(average)),
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(average / 10) * 100}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      mb: 1,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getDimensionColor(average),
                      },
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {insight.insight}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ) : (
        <Alert severity="info">
          Complete relationship analysis to view dimension insights.
        </Alert>
      )}
    </Box>
  );

  const renderRecommendationsTab = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Actionable Recommendations
      </Typography>

      <Stack spacing={2}>
        {/* High Priority Actions */}
        {insights.criticalUnconfirmed > 0 && (
          <Alert severity="error" action={
            <Button size="small" color="inherit">
              Review Critical
            </Button>
          }>
            <Typography variant="body2" fontWeight="bold">
              Address {insights.criticalUnconfirmed} critical unconfirmed relationships
            </Typography>
            <Typography variant="body2">
              These require immediate attention to prevent relationship deterioration.
            </Typography>
          </Alert>
        )}

        {insights.highConfidenceUnconfirmed > 0 && (
          <Alert severity="warning" action={
            <Button size="small" color="inherit">
              Confirm Now
            </Button>
          }>
            <Typography variant="body2" fontWeight="bold">
              Confirm {insights.highConfidenceUnconfirmed} high-confidence relationships
            </Typography>
            <Typography variant="body2">
              These are likely accurate and should be confirmed quickly.
            </Typography>
          </Alert>
        )}

        {/* Medium Priority Actions */}
        {insights.decliningTrends > 0 && (
          <Alert severity="info" action={
            <Button size="small" color="inherit">
              Investigate
            </Button>
          }>
            <Typography variant="body2" fontWeight="bold">
              Monitor {insights.decliningTrends} declining relationships
            </Typography>
            <Typography variant="body2">
              Consider intervention strategies to reverse negative trends.
            </Typography>
          </Alert>
        )}

        {/* Strategic Recommendations */}
        <Card variant="outlined">
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <IdeaIcon color="primary" />
              <Typography variant="subtitle2">
                Strategic Recommendations
              </Typography>
            </Box>

            <List dense>
              {insights.networkDensity < 10 && (
                <ListItem>
                  <ListItemIcon>
                    <NetworkIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Increase Network Connectivity"
                    secondary="Low network density suggests opportunities for new relationship discovery"
                  />
                </ListItem>
              )}

              {insights.confirmationRate < 70 && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Improve Confirmation Rate"
                    secondary="Focus on confirming high-confidence relationships to build trust in the system"
                  />
                </ListItem>
              )}

              {insights.averageHealth < 60 && (
                <ListItem>
                  <ListItemIcon>
                    <FlagIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Address Relationship Health"
                    secondary="Below-average health scores indicate need for relationship improvement initiatives"
                  />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );

  return (
    <Paper elevation={2}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InsightsIcon color="primary" />
            <Typography variant="h6">
              Network Insights
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Tabs
            value={activeTab}
            onChange={(e, newTab) => setActiveTab(newTab)}
            variant="fullWidth"
          >
            <Tab label="Overview" />
            <Tab label="Risks" />
            <Tab label="Opportunities" />
            <Tab label="Actions" />
          </Tabs>
        </Collapse>
      </Box>

      <Collapse in={expanded}>
        {activeTab === 0 && renderOverviewTab()}
        {activeTab === 1 && renderRisksTab()}
        {activeTab === 2 && renderOpportunitiesTab()}
        {activeTab === 3 && renderRecommendationsTab()}
      </Collapse>
    </Paper>
  );
}

export default NetworkInsightsPanel;